import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../../../lib/supabaseClient'
import { aggregateAccuracy, pickRoundQuestionIds, ROUND_SIZE } from './logic'

// Single source of truth for the pair's quiz state. Returns every
// round that's still in play or recently closed, plus all their
// answer rows, in one realtime-subscribed bundle. The page derives:
//   - myActive            — round the viewer is currently answering
//                           (FIFO oldest unfinished). Drives AnswerCard.
//   - waitingOnPartner[]  — rounds the viewer has fully answered but
//                           the partner hasn't. Drives the "play
//                           another while you wait" panel.
//   - revealsReady[]      — rounds where both have finished and the
//                           viewer hasn't dismissed the reveal yet.
//                           Drives the top-of-page reveal banner.
//
// The page (not the hook) owns dismissed-reveal tracking via
// localStorage, since dismissal is per-device and ephemeral.
//
// Lifecycle of a round:
//   1. created (anyone clicks a deck → insert)
//   2. one or both partners answering (quiz_answers rows accumulate)
//   3. both partners answer all questions → the
//      `quiz_auto_complete_round` DB trigger (migration 086) stamps
//      completed_at. From there it's a "reveal" until the viewer
//      dismisses it.
//   4. abandoned (manual stamp via abandonRound, completed_at set
//      without both sides finishing — round just disappears).
//
// Old PR-2-era rounds with completed_at set but incomplete answers
// (i.e. closed via the now-removed playAnotherRound path) will fall
// out of revealsReady because we gate it on "both have ROUND_SIZE
// answers", so they're effectively buried.
export function useQuizRounds(userId, partnerId) {
  const [rounds, setRounds] = useState([])
  const [answersByRound, setAnswersByRound] = useState(new Map())
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(async () => {
    if (!userId || !partnerId) return
    // We need: every open round for the pair + recently closed rounds
    // (so the reveal stash isn't lost if the page reloads after both
    // partners finished but before the viewer dismissed). 72h is a
    // generous cutoff — long enough that a weekend-offline partner
    // can still see Saturday's reveal on Monday, short enough that the
    // history accumulator (useAccuracyTally) stays cheap.
    const cutoff = new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString()

    const [{ data: openData }, { data: recentData }] = await Promise.all([
      supabase
        .from('quiz_rounds')
        .select('*')
        .or(`initiator_id.eq.${userId},partner_id.eq.${userId}`)
        .is('completed_at', null)
        .order('started_at', { ascending: true }),
      supabase
        .from('quiz_rounds')
        .select('*')
        .or(`initiator_id.eq.${userId},partner_id.eq.${userId}`)
        .gte('completed_at', cutoff)
        .order('completed_at', { ascending: false }),
    ])

    const merged = [...(openData || []), ...(recentData || [])]
    const seen = new Set()
    const rs = []
    for (const r of merged) {
      if (seen.has(r.id)) continue
      seen.add(r.id)
      rs.push(r)
    }

    let answers = []
    if (rs.length) {
      const { data: ansData } = await supabase
        .from('quiz_answers')
        .select('*')
        .in('round_id', rs.map((r) => r.id))
      answers = ansData || []
    }

    const map = new Map()
    for (const a of answers) {
      const arr = map.get(a.round_id) || []
      arr.push(a)
      map.set(a.round_id, arr)
    }

    setRounds(rs)
    setAnswersByRound(map)
  }, [userId, partnerId])

  useEffect(() => {
    if (!userId || !partnerId) {
      setRounds([]); setAnswersByRound(new Map()); setLoading(false); return undefined
    }
    let alive = true
    setLoading(true)

    // One channel, two table subscriptions. We don't filter
    // server-side because the OR(initiator,partner) shape is fiddly
    // for postgres_changes and the table is tiny — refetching on
    // every event is cheaper than perfect filters.
    const channel = supabase
      .channel(`quiz-pair-${userId}-${partnerId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'quiz_rounds' },
        async () => { if (alive) await refetch() },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'quiz_answers' },
        async () => { if (alive) await refetch() },
      )
      .subscribe()

    refetch().then(() => { if (alive) setLoading(false) })

    return () => { alive = false; supabase.removeChannel(channel) }
  }, [userId, partnerId, refetch])

  // Insert a fresh round for the pair. `category` is the deck name or
  // null/"all" for a mixed round. `pool` is the merged BANK + user
  // suggestions. `exclude` is the recent-questions set from
  // useRecentRoundQuestionIds.
  const startNewRound = useCallback(async (category = null, pool, exclude) => {
    if (!userId || !partnerId) return null
    const payload = {
      initiator_id: userId,
      partner_id: partnerId,
      question_ids: pickRoundQuestionIds(category, undefined, pool, exclude),
      category: category && category !== 'all' ? category : null,
    }
    const { data, error } = await supabase
      .from('quiz_rounds')
      .insert(payload)
      .select()
      .single()
    if (error || !data) return null
    // Optimistic insert so the page can switch to the new round
    // immediately; realtime echo will be a no-op via the id dedup
    // inside refetch.
    setRounds((curr) => (curr.some((r) => r.id === data.id) ? curr : [...curr, data]))
    return data
  }, [userId, partnerId])

  // Close a specific round without finishing it. Used for the per-
  // round "I don't want to wait on this one anymore" link in the
  // waiting panel, and as the escape hatch on the active round when
  // the user is stuck. Doesn't add to the reveals stash because the
  // reveal-readiness gate requires both sides to have ROUND_SIZE
  // answers.
  const abandonRound = useCallback(async (roundId) => {
    if (!roundId) return
    const now = new Date().toISOString()
    await supabase
      .from('quiz_rounds')
      .update({ completed_at: now })
      .eq('id', roundId)
      .is('completed_at', null)
    setRounds((curr) => curr.map((r) => (r.id === roundId ? { ...r, completed_at: now } : r)))
  }, [])

  // Counterpart to abandonRound — clears the completed_at stamp so an
  // accidentally-abandoned round comes back to the list. The DB trigger
  // for "both done" will re-stamp it naturally once the partner
  // catches up, so this is safe even if both sides had progress.
  const restoreRound = useCallback(async (roundId) => {
    if (!roundId) return
    await supabase
      .from('quiz_rounds')
      .update({ completed_at: null })
      .eq('id', roundId)
    setRounds((curr) => curr.map((r) => (r.id === roundId ? { ...r, completed_at: null } : r)))
  }, [])

  // Optimistic local push of an answer row — keeps the AnswerCard
  // moving past the just-submitted question without waiting for the
  // realtime echo (the original PR 2/3 "stuck on Q1" bug). The echo
  // is deduped via id in the refetch.
  const appendAnswer = useCallback((roundId, row) => {
    if (!roundId || !row?.id) return
    setAnswersByRound((m) => {
      const next = new Map(m)
      const arr = next.get(roundId) || []
      if (arr.some((a) => a.id === row.id)) return m
      next.set(roundId, [...arr, row])
      return next
    })
  }, [])

  return {
    rounds,
    answersByRound,
    loading,
    startNewRound,
    abandonRound,
    restoreRound,
    appendAnswer,
    refetch,
  }
}

// Running tally — every completed round between us, aggregated.
// Re-fetches when `refreshKey` changes so the page can bump it after
// the most recent round completes.
export function useAccuracyTally(userId, partnerId, refreshKey) {
  const [tally, setTally] = useState({ mineCorrect: 0, mineTotal: 0, theirsCorrect: 0, theirsTotal: 0 })

  useEffect(() => {
    if (!userId || !partnerId) return undefined
    let alive = true

    ;(async () => {
      const { data: rounds } = await supabase
        .from('quiz_rounds')
        .select('id, question_ids, completed_at')
        .or(`initiator_id.eq.${userId},partner_id.eq.${userId}`)
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false })
        .limit(40)
      const roundIds = (rounds || []).map((r) => r.id)
      if (!roundIds.length) {
        if (alive) setTally({ mineCorrect: 0, mineTotal: 0, theirsCorrect: 0, theirsTotal: 0 })
        return
      }
      const { data: answers } = await supabase
        .from('quiz_answers')
        .select('*')
        .in('round_id', roundIds)
      if (!alive) return
      setTally(aggregateAccuracy(rounds || [], answers || [], userId, partnerId))
    })()

    return () => { alive = false }
  }, [userId, partnerId, refreshKey])

  return tally
}

export { ROUND_SIZE }

// Set<question_id> of every question the pair has used in their last
// `rounds` rounds (regardless of completion). Used as the exclusion
// set when starting a new round so the next deck pick doesn't share
// half its questions with the round you just played. `rounds=3` =
// the last 15 question_ids on a 5-per-round game; with the bank at
// 159 entries that's ~9% of the total, low enough that fresh deck
// picks stay easy.
export function useRecentRoundQuestionIds(userId, partnerId, rounds = 3, refreshKey) {
  const [ids, setIds] = useState(new Set())

  useEffect(() => {
    if (!userId || !partnerId) { setIds(new Set()); return undefined }
    let alive = true
    supabase
      .from('quiz_rounds')
      .select('question_ids')
      .or(`initiator_id.eq.${userId},partner_id.eq.${userId}`)
      .order('started_at', { ascending: false })
      .limit(rounds)
      .then(({ data }) => {
        if (!alive) return
        const set = new Set()
        for (const r of data || []) {
          for (const qid of r.question_ids || []) set.add(qid)
        }
        setIds(set)
      })
    return () => { alive = false }
  }, [userId, partnerId, rounds, refreshKey])

  return ids
}
